import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { User, ApiError } from '../types';
import authService from '../services/auth';
import { LoginRequest, RegisterRequest, ResetPasswordRequest } from '../services/auth';
import guestService from '../services/guestService';

/** True if the error is due to network/server unavailability so we should not clear tokens. */
function isTransientOrNetworkError(error: unknown): boolean {
  if (error && typeof error === 'object' && 'code' in error) {
    const code = (error as { code?: string }).code;
    if (code === 'NETWORK_ERROR' || code === 'TIMEOUT') return true;
  }
  // Axios-style: no response means network failure
  if (error && typeof error === 'object' && 'response' in error) {
    if ((error as { response?: unknown }).response == null) return true;
  }
  return false;
}

interface AuthState {
  // User data
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  
  // Token management
  token: string | null;
  refreshToken: string | null;
  
  // Guest user management
  isGuest: boolean;
  guestUserId: string | null;
  
  // Session persistence
  isSessionRestored: boolean;
  
  // Error handling
  error: ApiError | null;
  
  // Actions
  login: (credentials: LoginRequest) => Promise<void>;
  register: (userData: RegisterRequest) => Promise<void>;
  logout: () => Promise<void>;
  resetPassword: (data: ResetPasswordRequest) => Promise<void>;
  refreshAuth: () => Promise<void>;
  restoreSession: () => Promise<void>;
  clearError: () => void;
  setUser: (user: User | null) => void;
  setToken: (token: string) => void;
  setGuestTokens: (token: string, refreshToken: string, guestUserId: string) => void;
  /** Transition from guest to authenticated after async login (e.g. waiting-update). Clears guest state and sets tokens without marking unauthenticated. */
  setAuthenticatedAfterGuestLogin: (token: string, refreshToken: string, user?: User | null) => void;
  clearGuestData: () => void;
  exchangeGuestToken: () => Promise<void>;
  ensureValidToken: () => Promise<boolean>;
}

const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      // Initial state
      user: null,
      isAuthenticated: false,
      isLoading: false,
      token: null,
      refreshToken: null,
      isGuest: false,
      guestUserId: null,
      isSessionRestored: false,
      error: null,

      // Actions
      login: async (credentials: LoginRequest) => {
        set({ isLoading: true, error: null });
        
        try {
          // Capture guest data before clearing so backend can transfer invite variables
          const guestData = guestService.getGuestData();
          const enrichedCredentials: LoginRequest = {
            ...credentials,
            ...(guestData && {
              guestUserId: guestData.userId,
              guestToken: guestData.token,
            }),
          };

          guestService.clearGuestData();
          
          const { user, token, refreshToken } = await authService.login(enrichedCredentials);
          
          set({
            user,
            token,
            refreshToken,
            isAuthenticated: true,
            isGuest: false,
            guestUserId: null,
            isLoading: false,
            error: null,
          });
          
        } catch (error) {
          const apiError = error as ApiError;
          set({
            isLoading: false,
            error: apiError,
            isAuthenticated: false,
          });
          throw error;
        }
      },

      register: async (userData: RegisterRequest) => {
        set({ isLoading: true, error: null });
        
        try {
          // Clear guest data before registration
          guestService.clearGuestData();
          
          const { user, token, refreshToken } = await authService.register(userData);
          
          set({
            user,
            token,
            refreshToken,
            isAuthenticated: true,
            isGuest: false,
            guestUserId: null,
            isLoading: false,
            error: null,
          });
          
        } catch (error) {
          const apiError = error as ApiError;
          set({
            isLoading: false,
            error: apiError,
            isAuthenticated: false,
          });
          throw error;
        }
      },

      logout: async () => {
        set({ isLoading: true });
        
        try {
          await authService.logout();
        } catch (error) {
          // Continue with logout even if server call fails
          console.warn('Logout server call failed:', error);
        } finally {
          // Clear guest data on logout
          guestService.clearGuestData();
          
          set({
            user: null,
            token: null,
            refreshToken: null,
            isAuthenticated: false,
            isGuest: false,
            guestUserId: null,
            error: null,
            isLoading: false,
          });
        }
      },

      resetPassword: async (data: ResetPasswordRequest) => {
        set({ isLoading: true, error: null });
        
        try {
          await authService.resetPassword(data);
          set({ isLoading: false, error: null });
        } catch (error) {
          const apiError = error as ApiError;
          set({
            isLoading: false,
            error: apiError,
          });
          throw error;
        }
      },

      refreshAuth: async () => {
        set({ isLoading: true });
        
        try {
          const isValid = await authService.ensureValidToken();
          
          if (isValid) {
            // Get updated user info to ensure we have the latest data
            const user = await authService.getCurrentUser();
            set({ 
              user,
              isLoading: false,
              isAuthenticated: true,
            });
          } else {
            throw new Error('Token refresh failed');
          }
        } catch (error) {
          set({ isLoading: false });
          get().logout();
          throw error;
        }
      },

      restoreSession: async () => {
        set({ isLoading: true });
        
        try {
          // First check for guest session
          const guestData = guestService.getGuestData();
          
          if (guestData) {
            // Check if guest token is still valid
            const isValid = await get().ensureValidToken();
            
            if (isValid) {
              // Restore guest session
              set({
                isGuest: true,
                guestUserId: guestData.userId,
                token: guestData.token,
                refreshToken: guestData.refreshToken,
                isAuthenticated: true, // Guest users are "authenticated" for API purposes
                isLoading: false,
                isSessionRestored: true,
                error: null,
              });
              return;
            } else {
              // Guest token expired, clear guest data
              guestService.clearGuestData();
            }
          }
          
          // Check if we have a regular refresh token
          const hasRefreshToken = authService.hasRefreshToken();
          
          if (hasRefreshToken) {
            // Try to refresh the JWT token
            const isValid = await authService.ensureValidToken();
            
            if (isValid) {
              // Get current user info
              const user = await authService.getCurrentUser();
              const token = authService.getCurrentToken();
              
              set({
                user,
                token,
                isAuthenticated: true,
                isGuest: false,
                guestUserId: null,
                isLoading: false,
                isSessionRestored: true,
                error: null,
              });
            } else {
              console.error('[AuthStore] restoreSession: Token validation failed');
              throw new Error('Session validation failed');
            }
          } else {
            // No refresh token available
            set({
              isLoading: false,
              isSessionRestored: true,
              isGuest: false,
              guestUserId: null,
            });
          }
        } catch (error) {
          console.error('[AuthStore] restoreSession: Error during restoration:', error);
          const isTransient = isTransientOrNetworkError(error);
          if (!isTransient) {
            // Auth failure (e.g. 401, invalid/expired refresh token): clear session
            get().logout();
          }
          set({
            isLoading: false,
            isSessionRestored: true,
            error: error as ApiError,
          });
        }
      },

      clearError: () => {
        set({ error: null });
      },

      setUser: (user: User | null) => {
        set({ user });
      },

      setToken: (token: string) => {
        set({ token });
      },

      setAuthenticatedAfterGuestLogin: (token: string, refreshToken: string, user?: User | null) => {
        guestService.clearGuestData();
        const state = get();
        set({
          token,
          refreshToken,
          isGuest: false,
          guestUserId: null,
          isAuthenticated: true,
          user: user !== undefined ? user : state.user,
        });
      },

      setGuestTokens: (token: string, refreshToken: string, guestUserId: string) => {
        // Store guest tokens in authService for API calls
        // Note: authService will need to be updated to handle guest tokens
        set({
          token,
          refreshToken,
          isGuest: true,
          guestUserId,
          isAuthenticated: true, // Guest users are "authenticated" for API purposes
        });
      },

      clearGuestData: () => {
        guestService.clearGuestData();
        const state = get();
        // When clearing guest, also clear guest tokens so retries create a new guest instead of reusing stale one
        if (state.isGuest) {
          set({
            isGuest: false,
            guestUserId: null,
            token: null,
            refreshToken: null,
            isAuthenticated: false,
          });
        } else {
          set({
            isGuest: false,
            guestUserId: null,
          });
        }
      },

      /**
       * Exchange guest token for user tokens after guest migration (e.g., after async login)
       */
      exchangeGuestToken: async () => {
        const state = get();
        
        if (!state.isGuest || !state.token) {
          throw new Error('Not a guest user or no token available');
        }

        set({ isLoading: true, error: null });

        try {
          // Pass the guest token explicitly since authService doesn't have it
          const { user, token, refreshToken } = await authService.exchangeGuestToken(state.token);
          
          // Clear guest data
          guestService.clearGuestData();
          
          set({
            user,
            token,
            refreshToken,
            isAuthenticated: true,
            isGuest: false,
            guestUserId: null,
            isLoading: false,
            error: null,
          });
        } catch (error) {
          const apiError = error as ApiError;
          const isMigrationNotComplete =
            apiError?.code === 'VALIDATION_ERROR' &&
            typeof apiError?.message === 'string' &&
            apiError.message.includes('not been migrated');
          if (isMigrationNotComplete) {
            console.warn('[AuthStore] exchangeGuestToken: Guest not migrated yet (will retry on next scene load):', apiError?.message);
          } else {
            console.error('[AuthStore] exchangeGuestToken: Error during exchange:', error);
          }
          set({
            isLoading: false,
            error: apiError,
          });
          throw error;
        }
      },

      ensureValidToken: async () => {
        try {
          const state = get();
          
          // If guest, use guest token refresh logic
          if (state.isGuest && state.guestUserId) {
            const guestData = guestService.getGuestData();
            if (!guestData) {
              return false;
            }
            
            // Check if guest token needs refresh (1 hour before 24-hour expiration)
            if (guestService.shouldRefreshGuestToken()) {
              const refreshed = await guestService.refreshGuestToken(guestData.refreshToken);
              if (refreshed) {
                get().setGuestTokens(refreshed.token, refreshed.refreshToken, refreshed.userId);
                return true;
              }
              return false;
            }
            
            // Token is still valid
            return true;
          }
          
          // Regular token validation
          return await authService.ensureValidToken();
        } catch (error) {
          console.error('Token validation failed:', error);
          return false;
        }
      },
    }),
    {
      name: 'auth-storage',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        // Only persist user data and authentication status
        // Tokens are managed by the auth service and guest service
        user: state.user,
        isAuthenticated: state.isAuthenticated,
        isGuest: state.isGuest,
        guestUserId: state.guestUserId,
      }),
    }
  )
);

export default useAuthStore;
